// LiveCore — the post-22:00 centrepiece. Wraps the counting-hall
// stream + the live blog in a side-by-side desktop layout and adds
// three interaction patterns the static markup couldn't give us:
//
//   1. Pin-on-scroll. A sentinel above the wrapper watches when
//      the user has scrolled past the live-core's top edge. Once
//      that happens, the stream slot is fixed to a free-floating
//      bottom-right mini-player (sized strictly to the video's
//      16:9 box, no extra chrome above or beside it) and the
//      live-blog auto-expands to fill the row width. Scrolling
//      back to the live-core unpins.
//
//   2. Drag-to-move. While pinned, a thin grab handle at the top
//      of the mini-player accepts pointer-drag to reposition the
//      whole container anywhere on screen. We don't grab from the
//      iframe itself because iframes swallow pointer events.
//
//   3. Fullscreen split. A top-right button enters the browser's
//      native fullscreen on the wrapper element; in :fullscreen
//      the layout flips to a strict 50/50 stream/feed split so
//      readers can sit on this view at a desk. ESC exits.
//
// Pin + fullscreen states are CSS-driven via the `is-stream-pinned`
// and `is-fullscreen` classes. Drag position is an inline style on
// the stream slot — null when at the default anchor, {x,y} once
// the user has moved it. Position resets when the mini unpins.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import './LiveCore.css';

interface Props {
  stream: ReactNode;
  feed: ReactNode;
  /** Optional content that stacks directly under the stream in the
   *  SAME grid column (left side on desktop). Used on /counting to
   *  put the results map under the counting-hall video at exactly
   *  the same width, with the live-feed scrolling past on the right.
   *
   *  When provided, the feed-expansion-on-pin behaviour is
   *  suppressed: the grid stays at its 1.6fr / 1fr split even after
   *  the stream is pinned to the corner, so the map content keeps
   *  its inline position instead of getting steamrolled by the feed
   *  claiming the full row. */
  belowStream?: ReactNode;
}

interface PinPos {
  x: number;
  y: number;
}

interface DragState {
  offsetX: number;
  offsetY: number;
  pointerId: number;
}

export default function LiveCore({ stream, feed, belowStream }: Props) {
  const hasLeftBelow = belowStream !== undefined && belowStream !== null;
  const containerRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const [streamPinned, setStreamPinned] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pinDismissed, setPinDismissed] = useState(false);
  const [pinPos, setPinPos] = useState<PinPos | null>(null);
  // Measured inline height of the stream slot — drives the sentinel's
  // Y so the pin only triggers after the user has scrolled past the
  // entire video, not the moment the live-core's top edge clears the
  // viewport. ResizeObserver keeps it accurate across viewport
  // changes (only updates while inline so the measurement isn't
  // contaminated by the mini's smaller height).
  const [streamHeight, setStreamHeight] = useState(0);

  // Pin-on-scroll: observe a sentinel that's absolutely positioned at
  // the BOTTOM of the stream's natural inline space inside the live-
  // core. The sentinel stays at the same document Y regardless of
  // whether the stream is inline or pinned — so the previous
  // toggle-loop (where pinning made the slot position:fixed inside
  // the viewport, IO saw it as "intersecting", and we immediately
  // un-pinned) can't happen.
  useEffect(() => {
    const slot = streamRef.current;
    if (!slot) return;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      // Only update while inline — once pinned the slot's height is
      // the mini's, which we don't want as our sentinel anchor.
      if (h > 0 && !streamPinned) setStreamHeight(h);
    });
    ro.observe(slot);
    return () => ro.disconnect();
  }, [streamPinned]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          setStreamPinned(true);
        } else if (entry.isIntersecting) {
          setStreamPinned(false);
          // Returning to inline view resets the user's overrides so the
          // next pin starts from the default bottom-right anchor.
          setPinDismissed(false);
          setPinPos(null);
        }
      },
      { threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  // Sync our state with the browser when native fullscreen exits
  // (ESC, browser button, etc). Only fires when native fullscreen
  // was actually used — for the CSS-only fallback path, the ESC
  // keydown handler below takes care of it.
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ESC closes our CSS-only fullscreen overlay. Native fullscreen
  // handles ESC itself, so we only act when no element is in the
  // browser's fullscreen state.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  function toggleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (isFullscreen) {
      // Exit — drop the class first, then exit native if applicable.
      setIsFullscreen(false);
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      return;
    }
    // Enter — apply the CSS class up-front so the user sees a
    // viewport overlay even if native fullscreen is rejected
    // (Facebook iframes inside the stream slot have done this in
    // testing). Native fullscreen is an upgrade on top, not a
    // requirement.
    setIsFullscreen(true);
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {
        /* swallow — CSS overlay already in place */
      });
    }
  }

  // ── Drag-to-move (pointer events on the handle bar) ────────────
  //
  // We capture pointer events on the handle (a thin overlay above
  // the iframe — the iframe itself swallows mouse events so the
  // wrapper can't be dragged directly from the video). On move we
  // apply the new {x, y} as inline style; on up we release capture.
  const onHandlePointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const target = streamRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    dragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }, []);

  const onHandlePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const next: PinPos = {
      x: e.clientX - drag.offsetX,
      y: e.clientY - drag.offsetY,
    };
    // Clamp to viewport so the user can't drag the mini fully off-
    // screen (we'd lose the handle and they'd be stuck). 8px margin.
    const el = streamRef.current;
    if (el) {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      next.x = Math.max(8, Math.min(window.innerWidth - w - 8, next.x));
      next.y = Math.max(8, Math.min(window.innerHeight - h - 8, next.y));
    }
    setPinPos(next);
  }, []);

  const onHandlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag && drag.pointerId === e.pointerId) {
      dragRef.current = null;
      try {
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
      } catch {
        /* element may have unmounted between down + up */
      }
    }
  }, []);

  const showPinnedStream = streamPinned && !pinDismissed;

  // Inline-style override for the mini's position once the user
  // has dragged it — overrides the default bottom-right CSS anchor.
  const streamStyle =
    showPinnedStream && pinPos
      ? {
          left: `${pinPos.x}px`,
          top: `${pinPos.y}px`,
          right: 'auto' as const,
          bottom: 'auto' as const,
        }
      : undefined;

  return (
    <div
      ref={containerRef}
      className={[
        'live-core',
        showPinnedStream ? 'is-stream-pinned' : '',
        isFullscreen ? 'is-fullscreen' : '',
        hasLeftBelow ? 'has-left-below' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
        {/* Pin-trigger sentinel — anchored at the natural bottom of
            the stream slot so the trigger fires only when the user
            has scrolled past the entire video. Absolutely positioned
            so its document Y doesn't shift when the stream goes
            fixed-corner. */}
        <div
          ref={sentinelRef}
          className="live-core__pin-sentinel"
          style={{ top: `${streamHeight}px` }}
          aria-hidden="true"
        />
        <div className="live-core__left">
        <div ref={streamRef} className="live-core__stream" style={streamStyle}>
          {/* Drag handle — only meaningful while pinned, sits over
              the top edge of the mini. Uses pointer events for
              cross-input (mouse, touch, pen) drag. */}
          {showPinnedStream && (
            <div
              className="live-core__drag-handle"
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={onHandlePointerUp}
              onPointerCancel={onHandlePointerUp}
              role="button"
              aria-label="Drag to reposition stream"
              tabIndex={0}
            >
              <span className="live-core__drag-grip" aria-hidden="true">⋮⋮</span>
              <span className="live-core__drag-label">Counting stream</span>
              <button
                type="button"
                className="live-core__pin-dismiss"
                onClick={(ev) => {
                  ev.stopPropagation();
                  setPinDismissed(true);
                }}
                onPointerDown={(ev) => ev.stopPropagation()}
                aria-label="Hide pinned stream"
                title="Hide pinned stream"
              >
                ✕
              </button>
            </div>
          )}
          {stream}
        </div>
        {/* Optional stack-under-stream slot (e.g. the counting-phase
            results map). Stays in flow inside the left grid column
            even when the stream is pinned to the corner; the feed
            scrolls past it on the right.

            When the stream pins to the corner it leaves the flex
            column, which would otherwise let the map slide up by
            the stream's full height — visually annoying because the
            map jumps the moment the user scrolls past the video.
            Reserving padding-top equal to the stream's measured
            height (plus the 20px flex gap) keeps the map at its
            original viewport position. */}
        {hasLeftBelow && (
          <div
            className="live-core__left-below"
            style={
              showPinnedStream && streamHeight > 0
                ? { paddingTop: `${streamHeight + 20}px` }
                : undefined
            }
          >
            {belowStream}
          </div>
        )}
        </div>
        <div className="live-core__feed">{feed}</div>

        {/* Top-right fullscreen toggle — visible inline AND in
            :fullscreen. The button keeps its absolute positioning
            either way so the user can always exit. */}
        <button
          type="button"
          className="live-core__fullscreen-toggle"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Open fullscreen split view'}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen 50/50 split'}
        >
          {isFullscreen ? '⤡ Exit fullscreen' : '⛶ Fullscreen split'}
        </button>
    </div>
  );
}
